import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { selectIsAuthenticated } from "@onmo/auth";
import {
  askProfilePin,
  clearProfileSession,
  getIsLoggingOut,
  getIsProfileSwitching,
  getProfileChosenAt,
  getProfileLastActiveAt,
  releaseLoggingOut,
  setProfileSession,
  subscribeProfileSession,
  touchProfileActivity,
} from "@onmo/graphql";
import {
  Profile as ProfileGQL,
  ProfilePolicy,
  Query,
} from "@onmo/graphql/graphql";
import {
  generateProfileTokenGQL,
  meProfileGQL,
  profilesGQL,
  profilePolicyGQL,
} from "../graphql";
import { useAppSelector } from "../components/hooks";
import { PageRoutes } from "../constants/routes";
import { toProfile } from "../models/profile";
import { getBackendDeviceType } from "../utils/deviceDetection";

const ACTIVITY_INTERVAL_MS = 60 * 1000;

export const useIsProfileSwitching = () =>
  useSyncExternalStore(subscribeProfileSession, getIsProfileSwitching);

export const useCurrentProfile = (profileId?: string) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { data: meData } = useQuery<{ me: Query["me"] }>(meProfileGQL, {
    skip: !isAuthenticated,
  });
  const { data, loading } = useQuery<{ profiles: ProfileGQL[] }>(profilesGQL, {
    skip: !isAuthenticated,
    context: { skipProfileToken: true },
  });
  const profiles = data?.profiles ?? [];
  const current =
    meData?.me.profile ?? profiles.find((profile) => profile.isMainAccount);
  const profileById = profileId
    ? profiles.find((profile) => profile.id === profileId)
    : undefined;
  return {
    currentProfile: current ? toProfile(current) : undefined,
    profileById: profileById ? toProfile(profileById) : undefined,
    isLoading: loading,
  };
};

// Kids have no access to Install & Play or Steam
export const useIsKidProfile = () =>
  !!useCurrentProfile().currentProfile?.isKid;

export const useIsMainProfile = () =>
  useCurrentProfile().currentProfile?.isOwner ?? true;

type TimeoutResult = "keepProfile" | "chooseProfile" | "enterPin";

const getTimeoutResult = (
  awaySeconds: number,
  policy: ProfilePolicy,
  isLocked: boolean,
): TimeoutResult => {
  const profileTimeout = policy.tokenRefreshRules.find(
    (rule) => rule.deviceType === getBackendDeviceType(),
  )?.refreshSeconds;
  if (profileTimeout != null && awaySeconds > profileTimeout) {
    return "chooseProfile";
  }
  if (isLocked && awaySeconds > policy.pinRefreshSeconds) return "enterPin";
  return "keepProfile";
};

const getLeftAt = () =>
  getProfileChosenAt() ? getProfileLastActiveAt() : null;

const useProfileTimeout = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { currentProfile } = useCurrentProfile();
  const { data, error } = useQuery<{ config: Query["config"] }>(
    profilePolicyGQL,
    { skip: !isAuthenticated },
  );
  const policy = data?.config.profilePolicy;
  const [leftAt, setLeftAt] = useState(getLeftAt);
  const [isCheckingOnStart, setIsCheckingOnStart] = useState(leftAt !== null);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") touchProfileActivity();
      else setLeftAt(getLeftAt());
    };
    // Keeps the time fresh for when the app is killed without any event
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") touchProfileActivity();
    }, ACTIVITY_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", touchProfileActivity);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", touchProfileActivity);
    };
  }, []);

  useEffect(() => {
    if (leftAt === null || !policy || !currentProfile) return;
    const awaySeconds = (Date.now() - leftAt) / 1000;
    const result = getTimeoutResult(
      awaySeconds,
      policy,
      currentProfile.hasLock,
    );
    setLeftAt(null);
    setIsCheckingOnStart(false);
    touchProfileActivity();
    if (result === "chooseProfile") clearProfileSession();
    if (result === "enterPin") askProfilePin(currentProfile.id);
  }, [leftAt, policy, currentProfile]);

  return isAuthenticated && isCheckingOnStart && !error;
};

export const useProfilePickerRedirect = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const chosenAt = useSyncExternalStore(
    subscribeProfileSession,
    getProfileChosenAt,
  );
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const isLoggingOut = useSyncExternalStore(
    subscribeProfileSession,
    getIsLoggingOut,
  );
  const hasNotChosen = isAuthenticated && !chosenAt && !isLoggingOut;
  const isCheckingTimeout = useProfileTimeout();

  useEffect(() => {
    if (!isAuthenticated) releaseLoggingOut();
  }, [isAuthenticated]);
  const { data, error } = useQuery<{ profiles: ProfileGQL[] }>(profilesGQL, {
    skip: !hasNotChosen,
    context: { skipProfileToken: true },
  });
  const profileCount = data?.profiles.length;
  const isOnPicker = pathname === PageRoutes.WhoIsPlaying;
  const hasChoice = profileCount !== undefined && profileCount > 1;
  const hasNoChoice = profileCount !== undefined && profileCount <= 1;
  const mainProfileId = data?.profiles.find(
    (profile) => profile.isMainAccount,
  )?.id;
  const [generateProfileToken] = useMutation<{
    generateProfileToken: { token: string };
  }>(generateProfileTokenGQL, { context: { skipProfileToken: true } });
  const isEnteringMain = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || isOnPicker || !hasNotChosen) return;
    const goToPicker = () => {
      navigate(PageRoutes.WhoIsPlaying, {
        replace: true,
        state: { from: pathname + search },
      });
    };
    if (hasChoice) return goToPicker();
    if (!hasNoChoice || !mainProfileId || isEnteringMain.current) return;
    isEnteringMain.current = true;
    generateProfileToken({ variables: { input: { id: mainProfileId } } })
      .then((result) => {
        const token = result.data?.generateProfileToken?.token;
        if (!token) throw new Error("No profile token returned");
        setProfileSession(token);
      })
      .catch(goToPicker)
      .finally(() => {
        isEnteringMain.current = false;
      });
  }, [
    isAuthenticated,
    hasNotChosen,
    hasChoice,
    hasNoChoice,
    mainProfileId,
    isOnPicker,
    pathname,
    search,
    navigate,
    generateProfileToken,
  ]);

  return (hasNotChosen || isCheckingTimeout) && !isOnPicker && !error;
};
